import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/** Stands in for "no topic" — a Radix select item needs a non-empty value. */
const NONE = '__none__'

export interface ElectionTopicSelectProps {
  /** Topic name, or '' when none is set. */
  value: string
  onChange: (topic: string) => void
  /** Topics the advertiser may book against the chosen election. */
  topics: string[]
  loading?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Picks the topic within an election a political message is about. A topic the
 * advertiser no longer offers is still listed while it is the current value,
 * so opening an older line item does not silently drop its topic.
 */
export function ElectionTopicSelect({
  value,
  onChange,
  topics,
  loading,
  disabled,
  className,
}: ElectionTopicSelectProps) {
  const { t } = useTranslation()
  const options = value && !topics.includes(value) ? [value, ...topics] : topics

  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(v) => onChange(v === NONE ? '' : (v ?? ''))}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={t('political.topicPlaceholder')}>{value || null}</SelectValue>
      </SelectTrigger>
      <SelectContent loading={loading}>
        <SelectItem value={NONE}>
          <span className="text-muted-foreground">{t('political.noTopic')}</span>
        </SelectItem>
        {options.map((topic) => (
          <SelectItem key={topic} value={topic}>
            {topic}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
